import type { PlayRecord } from "@slime/contracts";

/** 由记录流算出的会话级指标。全部是「入队被接受」的记录数，不代表已收到服务端持久化确认。 */
export interface RecordSummary {
  /** 已接受入队的记录数（事件 + 快照 + 环境）。 */
  records: number;
  snapshots: number;
  attemptsStarted: number;
  cleared: number;
  failed: number;
  aborted: number;
  entitiesDied: number;
  tuningChanges: number;
  /** 当前尝试是否为调参局：开局 overlay 非空或局中有 tuning_changed（决策 0017 第五条）。 */
  currentAttemptTuned: boolean;
}

/**
 * 诊断聚合器（决策 0017 第八条）：面板指标从记录流计算，不另开旁路统计。
 * 面板与后端从同一种事实聚合；网络失败或后续丢弃可能使计数不同。
 * 只观察被 RecordTransport 接受入队的记录；交付结果由 transport diagnostics 单独显示。
 */
export class RecordAggregator {
  private readonly summary: RecordSummary = {
    records: 0,
    snapshots: 0,
    attemptsStarted: 0,
    cleared: 0,
    failed: 0,
    aborted: 0,
    entitiesDied: 0,
    tuningChanges: 0,
    currentAttemptTuned: false,
  };

  reset(): void {
    Object.assign(this.summary, {
      records: 0,
      snapshots: 0,
      attemptsStarted: 0,
      cleared: 0,
      failed: 0,
      aborted: 0,
      entitiesDied: 0,
      tuningChanges: 0,
      currentAttemptTuned: false,
    });
  }

  observe(record: PlayRecord): void {
    const s = this.summary;
    s.records += 1;
    if (record.kind === "snapshot") {
      s.snapshots += 1;
      return;
    }
    if (record.kind !== "event") return;
    const e = record.data;
    if (e.type === "system" && e.name === "attempt_started") {
      s.attemptsStarted++;
      const tuning = e.payload.context.tuning;
      s.currentAttemptTuned = !!(
        tuning &&
        typeof tuning === "object" &&
        !Array.isArray(tuning) &&
        tuning.overlay &&
        typeof tuning.overlay === "object" &&
        Object.keys(tuning.overlay).length
      );
    }
    if (e.type === "system" && e.name === "attempt_ended") {
      if (e.payload.result === "success") s.cleared++;
      else if (e.payload.result === "failure") s.failed++;
      else s.aborted++;
    }
    if (e.type === "game" && e.name === "entity:died") s.entitiesDied++;
    if (e.type === "game" && e.name === "tuning:changed") {
      s.tuningChanges++;
      s.currentAttemptTuned = true;
    }
  }

  snapshot(): Readonly<RecordSummary> {
    return { ...this.summary };
  }
}
